import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@material-ui/styles';
import {
  ButtonGroup,
  Button,
  Divider,
  List
} from '@material-ui/core';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import { Navigation, Modal, Popover } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    backgroundColor: theme.palette.white
  },
  toolbar: {
    padding: theme.spacing(2, 3),
    textAlign: 'center',
    height: 68
  },
  addIcon: {
    marginRight: theme.spacing(1)
  },
  addButton: {
    maxWidth: 231
  },
  button: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    fontWeight: theme.typography.fontWeightRegular
  },
  folderIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.icon
  },
  totalItems: {
    marginLeft: theme.spacing(1)
  },
  newItems: {
    marginLeft: 'auto'
  },
  list: {
    height: 'calc(100% - 69px)',
    overflowY: 'auto'
  },
  active: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.primary.main,
    '& $folderIcon': {
      color: theme.palette.primary.main
    }
  }
}));

const Folders = props => {
  const { className, folders, createFolder, createGroup, deleteFolder, duplicateGroup, onFolderOpen, onDropFolder, ...rest } = props;

  const classes = useStyles();
  const newFolderRef = useRef(null);
  const [openPopover, setOpenPopover] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const handleSelect = (parent) => (group) => {
    onFolderOpen && onFolderOpen(group, parent);
  };

  const handleOverDrop = (folder) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDropFolder && folder !== Number(e.dataTransfer.getData('folder'))) {
      onDropFolder(folder, Number(e.dataTransfer.getData('id')));
      if (e.clear) e.clear();
    }
  };

  return (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
      <div className={classes.toolbar}>
        <ButtonGroup ref={newFolderRef}>
          <Button
            className={classes.addButton}
            color="primary"
            fullWidth
            onClick={() => setOpenPopover(true)}
            variant="contained"
          >
            <CreateNewFolderIcon className={classes.addIcon} />
            Folder
          </Button>
          <Button
            className={classes.addButton}
            color="primary"
            fullWidth
            onClick={() => setOpenModal(true)}
            variant="contained"
          >
            <GroupAddIcon className={classes.addIcon} />
            Group
          </Button>
        </ButtonGroup>
      </div>
      <Divider />
      <List
        className={classes.list}
        onDragOver={e => e.preventDefault()}
        onDrop={handleOverDrop(0)}
      >
        <PerfectScrollbar>
          <Navigation
            component="div"
            deleteFolder={deleteFolder}
            handleDuplicateGroup={duplicateGroup}
            handleOverDrop={handleOverDrop}
            handleSelect={handleSelect}
            onOpenModal={setOpenModal}
            pages={folders}
          />
        </PerfectScrollbar>
      </List>
      <Modal
        onClose={() => setOpenModal(false)}
        onCreateGroup={createGroup}
        open={openModal}
      />
      <Popover
        anchorEl={newFolderRef.current}
        arrow
        onClose={() => setOpenPopover(false)}
        onSubmit={createFolder}
        open={openPopover}
        title="Add new folder"
      />
    </div>
  );
};

Folders.propTypes = {
  className: PropTypes.string,
  createFolder: PropTypes.func,
  createGroup: PropTypes.func,
  deleteFolder: PropTypes.func,
  duplicateGroup: PropTypes.func,
  folders: PropTypes.array,
  onDropFolder: PropTypes.func,
  onFolderOpen: PropTypes.func
};

export default Folders;
